import { LoaderCircle, X } from 'lucide-react';

import { Button } from '@heidi/design-system/button';
import { CircularProgress } from '@heidi/design-system/circular-progress';
import { cn } from '@heidi/utils';
import { useIntl } from 'react-intl';

import { projectComposerLoadingFileCardMessages } from './ProjectComposerLoadingFileCard.messages';

import type { JSX } from 'react';

interface ProjectComposerLoadingFileCardProps {
  fileName: string;
  fileType: string;
  loadingProgress?: number;
  onDelete?: () => void;
}

/**
 * Loading attachment chip for the Projects composer. Keeps file labels
 * visible during upload without extending the design-system FileCard API.
 */
export const ProjectComposerLoadingFileCard = ({
  fileName,
  fileType,
  loadingProgress,
  onDelete,
}: ProjectComposerLoadingFileCardProps): JSX.Element => {
  const intl = useIntl();
  const hasProgress = loadingProgress !== undefined;

  return (
    <div className="group relative flex h-10 w-44 items-center gap-2.5 rounded-lg border border-outline bg-surface py-1 pe-2 ps-1">
      <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-tertiary">
        {hasProgress ? (
          <CircularProgress progress={loadingProgress} size="sm" />
        ) : (
          <LoaderCircle className="size-4 animate-spin text-text-secondary" />
        )}
      </div>
      <div className="flex min-w-0 flex-[1_0_0] flex-col items-start gap-1 overflow-hidden pt-0.5">
        <p className="min-w-0 self-stretch truncate text-xs font-medium leading-tight text-text-primary">
          {fileName}
        </p>
        <p className="min-w-0 self-stretch truncate text-[10px] leading-tight text-text-secondary">
          {fileType ||
            intl.formatMessage(projectComposerLoadingFileCardMessages.unknownType)}
        </p>
      </div>
      {onDelete ? (
        <Button
          iconOnly
          className={cn(
            'absolute end-0 top-0 size-5 min-w-5 -translate-y-1/3 translate-x-1/3 rounded-full p-0.5 opacity-0',
            'after:absolute after:-end-1 after:-top-1 after:bottom-0 after:start-0 after:content-[""]',
            'hover:opacity-100 focus:opacity-100 group-hover:opacity-100 rtl:-translate-x-1/3'
          )}
          size="xxs"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          aria-label={intl.formatMessage(
            projectComposerLoadingFileCardMessages.deleteFile
          )}
          data-testid="file-card-delete-button"
          variant="destructive"
        >
          <X />
        </Button>
      ) : null}
    </div>
  );
};
